import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioTravelDateObject from '@/entities/ratioTravelDate/interface'
import ratioAccommodationObject from '@/entities/ratioAccommodation/interface'
import ratioExtensionAccommodationObject from '@/entities/ratioExtensionAccommodation/interface'
import ratioFamilyPackageObject from '@/entities/ratioFamilyPackage/interface'
import ratioPersonalAddonObject from '@/entities/ratioPersonalAddon/interface'
import ratioAccommodationAddonObject from '@/entities/ratioAccommodationAddon/interface'
import ratioBookingAddonObject from '@/entities/ratioBookingAddon/interface'
import ratioCollectiveAddonObject from '@/entities/ratioCollectiveAddon/interface'
import ratioTravelExtensionObject from '@/entities/ratioTravelExtension/interface'
import ratioPersonalDiscountObject from '@/entities/ratioPersonalDiscount/interface'

import {
  ALL_TRAVELS_QUERY,
  SINGLE_TRAVEL_QUERY,
  TRAVELS_DEPENDENTS_QUERY,
  CREATE_TRAVEL_MUTATION,
  UPDATE_TRAVEL_MUTATION,
  DELETE_TRAVEL_MUTATION,
  FORCE_DELETE_TRAVEL_MUTATION,
  ADD_TRAVEL_DATES_MUTATION,
  REMOVE_TRAVEL_DATES_MUTATION,
  CREATE_TRAVEL_DATES_MUTATION,
  DELETE_TRAVEL_DATES_MUTATION,
  UPDATE_TRAVEL_DATES_ORDER_MUTATION,
  ADD_TRAVEL_ACCOMMODATIONS_MUTATION,
  REMOVE_TRAVEL_ACCOMMODATIONS_MUTATION,
  CREATE_TRAVEL_ACCOMMODATIONS_MUTATION,
  DELETE_TRAVEL_ACCOMMODATIONS_MUTATION,
  UPDATE_TRAVEL_ACCOMMODATIONS_ORDER_MUTATION,
  ADD_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION,
  REMOVE_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION,
  CREATE_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION,
  DELETE_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION,
  UPDATE_TRAVEL_EXTENSION_ACCOMMODATIONS_ORDER_MUTATION,
  ADD_TRAVEL_FAMILY_PACKAGES_MUTATION,
  REMOVE_TRAVEL_FAMILY_PACKAGES_MUTATION,
  CREATE_TRAVEL_FAMILY_PACKAGES_MUTATION,
  DELETE_TRAVEL_FAMILY_PACKAGES_MUTATION,
  UPDATE_TRAVEL_FAMILY_PACKAGES_ORDER_MUTATION,
  ADD_TRAVEL_PERSONAL_ADDONS_MUTATION,
  REMOVE_TRAVEL_PERSONAL_ADDONS_MUTATION,
  CREATE_TRAVEL_PERSONAL_ADDONS_MUTATION,
  DELETE_TRAVEL_PERSONAL_ADDONS_MUTATION,
  UPDATE_TRAVEL_PERSONAL_ADDONS_ORDER_MUTATION,
  ADD_TRAVEL_ACCOMMODATION_ADDONS_MUTATION,
  REMOVE_TRAVEL_ACCOMMODATION_ADDONS_MUTATION,
  CREATE_TRAVEL_ACCOMMODATION_ADDONS_MUTATION,
  DELETE_TRAVEL_ACCOMMODATION_ADDONS_MUTATION,
  UPDATE_TRAVEL_ACCOMMODATION_ADDONS_ORDER_MUTATION,
  ADD_TRAVEL_BOOKING_ADDONS_MUTATION,
  REMOVE_TRAVEL_BOOKING_ADDONS_MUTATION,
  CREATE_TRAVEL_BOOKING_ADDONS_MUTATION,
  DELETE_TRAVEL_BOOKING_ADDONS_MUTATION,
  UPDATE_TRAVEL_BOOKING_ADDONS_ORDER_MUTATION,
  ADD_TRAVEL_COLLECTIVE_ADDONS_MUTATION,
  REMOVE_TRAVEL_COLLECTIVE_ADDONS_MUTATION,
  CREATE_TRAVEL_COLLECTIVE_ADDONS_MUTATION,
  DELETE_TRAVEL_COLLECTIVE_ADDONS_MUTATION,
  UPDATE_TRAVEL_COLLECTIVE_ADDONS_ORDER_MUTATION,
  ADD_TRAVEL_EXTENSIONS_MUTATION,
  REMOVE_TRAVEL_EXTENSIONS_MUTATION,
  CREATE_TRAVEL_EXTENSIONS_MUTATION,
  DELETE_TRAVEL_EXTENSIONS_MUTATION,
  UPDATE_TRAVEL_EXTENSIONS_ORDER_MUTATION,
  ADD_TRAVEL_PERSONAL_DISCOUNTS_MUTATION,
  REMOVE_TRAVEL_PERSONAL_DISCOUNTS_MUTATION,
  CREATE_TRAVEL_PERSONAL_DISCOUNTS_MUTATION,
  DELETE_TRAVEL_PERSONAL_DISCOUNTS_MUTATION,
  UPDATE_TRAVEL_PERSONAL_DISCOUNTS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioTravelRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TRAVEL_QUERY, variables).then((data: any) => {
      return hydrateObject(data.travel)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TRAVELS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.travels)
    })
  },

  useObject: (id: string | number, includes = {}) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR([SINGLE_TRAVEL_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.travel),
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR([ALL_TRAVELS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.travels),
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(TRAVELS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([TRAVELS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TRAVEL_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travel)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TRAVEL_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travel)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TRAVEL_MUTATION, variables).then((data: any) => {
      return data.deleteRatioTravel
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TRAVEL_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioTravel
    })
  },

  assignDates: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        dateIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_DATES_MUTATION, variables)
  },

  unassignDates: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        dateIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_DATES_MUTATION, variables)
  },

  createDates: async (id: string, items: Array<ratioTravelDateObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        dates: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_DATES_MUTATION, variables)
  },

  deleteDates: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        dateIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_DATES_MUTATION, variables)
  },

  updateDatesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        dateIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_DATES_ORDER_MUTATION, variables)
  },

  assignAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  unassignAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  createAccommodations: async (id: string, items: Array<ratioAccommodationObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodations: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  deleteAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  updateAccommodationsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_ACCOMMODATIONS_ORDER_MUTATION, variables)
  },

  assignExtensionAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionAccommodationIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION, variables)
  },

  unassignExtensionAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionAccommodationIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION, variables)
  },

  createExtensionAccommodations: async (id: string, items: Array<ratioExtensionAccommodationObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionAccommodations: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION, variables)
  },

  deleteExtensionAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionAccommodationIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_EXTENSION_ACCOMMODATIONS_MUTATION, variables)
  },

  updateExtensionAccommodationsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionAccommodationIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_EXTENSION_ACCOMMODATIONS_ORDER_MUTATION, variables)
  },

  assignFamilyPackages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackageIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_FAMILY_PACKAGES_MUTATION, variables)
  },

  unassignFamilyPackages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackageIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_FAMILY_PACKAGES_MUTATION, variables)
  },

  createFamilyPackages: async (id: string, items: Array<ratioFamilyPackageObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackages: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_FAMILY_PACKAGES_MUTATION, variables)
  },

  deleteFamilyPackages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackageIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_FAMILY_PACKAGES_MUTATION, variables)
  },

  updateFamilyPackagesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackageIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_FAMILY_PACKAGES_ORDER_MUTATION, variables)
  },

  assignPersonalAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_PERSONAL_ADDONS_MUTATION, variables)
  },

  unassignPersonalAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_PERSONAL_ADDONS_MUTATION, variables)
  },

  createPersonalAddons: async (id: string, items: Array<ratioPersonalAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddons: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_PERSONAL_ADDONS_MUTATION, variables)
  },

  deletePersonalAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_PERSONAL_ADDONS_MUTATION, variables)
  },

  updatePersonalAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_PERSONAL_ADDONS_ORDER_MUTATION, variables)
  },

  assignAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  unassignAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  createAccommodationAddons: async (id: string, items: Array<ratioAccommodationAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddons: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  deleteAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  updateAccommodationAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_ACCOMMODATION_ADDONS_ORDER_MUTATION, variables)
  },

  assignBookingAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_BOOKING_ADDONS_MUTATION, variables)
  },

  unassignBookingAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_BOOKING_ADDONS_MUTATION, variables)
  },

  createBookingAddons: async (id: string, items: Array<ratioBookingAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddons: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_BOOKING_ADDONS_MUTATION, variables)
  },

  deleteBookingAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_BOOKING_ADDONS_MUTATION, variables)
  },

  updateBookingAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_BOOKING_ADDONS_ORDER_MUTATION, variables)
  },

  assignCollectiveAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        collectiveAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_COLLECTIVE_ADDONS_MUTATION, variables)
  },

  unassignCollectiveAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        collectiveAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_COLLECTIVE_ADDONS_MUTATION, variables)
  },

  createCollectiveAddons: async (id: string, items: Array<ratioCollectiveAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        collectiveAddons: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_COLLECTIVE_ADDONS_MUTATION, variables)
  },

  deleteCollectiveAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        collectiveAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_COLLECTIVE_ADDONS_MUTATION, variables)
  },

  updateCollectiveAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        collectiveAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_COLLECTIVE_ADDONS_ORDER_MUTATION, variables)
  },

  assignExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  unassignExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  createExtensions: async (id: string, items: Array<ratioTravelExtensionObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensions: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  deleteExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  updateExtensionsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_EXTENSIONS_ORDER_MUTATION, variables)
  },

  assignPersonalDiscounts: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalDiscountIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_PERSONAL_DISCOUNTS_MUTATION, variables)
  },

  unassignPersonalDiscounts: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalDiscountIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_PERSONAL_DISCOUNTS_MUTATION, variables)
  },

  createPersonalDiscounts: async (id: string, items: Array<ratioPersonalDiscountObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalDiscounts: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_PERSONAL_DISCOUNTS_MUTATION, variables)
  },

  deletePersonalDiscounts: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalDiscountIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_PERSONAL_DISCOUNTS_MUTATION, variables)
  },

  updatePersonalDiscountsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalDiscountIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_PERSONAL_DISCOUNTS_ORDER_MUTATION, variables)
  },
}

export default ratioTravelRepository
